import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({
  user: null,
  userProfile: null,
  loading: true,
  profileLoading: false,
  signIn: async () => {},
  signUp: async () => {},
  signOut: async () => {},
  getUserProfile: async () => {}
});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Robust helper: always returns a readable string from any error shape
const extractErrorMessage = (err) => {
  if (!err) return 'Une erreur est survenue. Veuillez réessayer.';
  if (typeof err === 'string' && err?.trim() !== '') return err;
  if (typeof err === 'object') {
    const msg = err?.message || err?.error_description || err?.msg || err?.error;
    if (typeof msg === 'string' && msg?.trim() !== '') return msg;
    // Last resort: try JSON but avoid returning bare "{}"
    try {
      const json = JSON.stringify(err);
      if (json && json !== '{}' && json !== 'null') return json;
    } catch (_) {}
  }
  return 'Une erreur est survenue. Veuillez réessayer.';
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileLoading, setProfileLoading] = useState(false);

  // ✅ REQUIRED: Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return;
      setProfileLoading(true);
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
        if (!error && data) {
          setUserProfile(data);
        } else if (error?.code === 'PGRST116') {
          // Profile doesn't exist yet — create it from auth user metadata
          const { data: { user } } = await supabase?.auth?.getUser();
          if (user) {
            const { data: newProfile } = await supabase?.from('user_profiles')?.upsert({
              id: user?.id,
              email: user?.email,
              full_name: user?.user_metadata?.full_name || user?.email?.split('@')?.[0] || '',role: 'client',
              is_active: true,
            }, { onConflict: 'id' })?.select()?.single();
            if (newProfile) setUserProfile(newProfile);
          }
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      } finally {
        setProfileLoading(false);
      }
    },
    
    clear() {
      setUserProfile(null);
      setProfileLoading(false);
    }
  };

  // ✅ REQUIRED: Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null);
      setLoading(false);
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id); // Fire-and-forget
      } else {
        profileOperations?.clear();
      }
    }
  };

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      authStateHandlers?.onChange(null, session);
    });

    // PROTECTED: Never modify this callback signature
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    );

    return () => subscription?.unsubscribe();
  }, []);

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        if (error?.message?.toLowerCase()?.includes('captcha')) {
          return { 
            data: null, 
            error: 'La protection CAPTCHA est activée sur ce projet Supabase. Veuillez la désactiver dans le tableau de bord Supabase : Authentication → Settings → désactiver "Enable CAPTCHA protection".'
          };
        }
        return { data: null, error: extractErrorMessage(error) };
      }
      
      return { data, error: null };
    } catch (err) {
      if (err?.message?.includes('Failed to fetch')) {
        return { 
          data: null, 
          error: 'Impossible de se connecter au service d\'authentification. Votre projet Supabase est peut-être en pause.'
        };
      }
      return { data: null, error: extractErrorMessage(err) };
    }
  };

  const signUp = async (email, password, additionalData = {}) => {
    try {
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: additionalData
        }
      });
      
      if (error) {
        if (error?.message?.toLowerCase()?.includes('captcha')) {
          return { 
            data: null, 
            error: 'La protection CAPTCHA est activée sur ce projet Supabase. Veuillez la désactiver dans le tableau de bord Supabase : Authentication → Settings → désactiver "Enable CAPTCHA protection".'
          };
        }
        return { data: null, error: extractErrorMessage(error) };
      }
      
      return { data, error: null };
    } catch (err) {
      if (err?.message?.includes('Failed to fetch')) {
        return { 
          data: null, 
          error: 'Impossible de se connecter au service d\'authentification. Votre projet Supabase est peut-être en pause.'
        };
      }
      return { data: null, error: extractErrorMessage(err) };
    }
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) {
        return { error: error?.message };
      }
      return { error: null };
    } catch (error) {
      return { error: 'Failed to sign out' };
    }
  };

  const getUserProfile = async () => {
    if (!user?.id) return { data: null, error: 'No authenticated user' };
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', user?.id)?.single();
      
      if (error) {
        return { data: null, error: error?.message };
      }
      
      setUserProfile(data);
      return { data, error: null };
    } catch (error) {
      return { data: null, error: 'Failed to fetch user profile' };
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    profileLoading,
    signIn,
    signUp,
    signOut,
    getUserProfile
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;